import { useMutation, useQuery } from "@tanstack/react-query";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useEffect } from "react";

import { api } from "../lib/api";
import type { User } from "../lib/types";
import { useAdminAuthStore, useSellerAuthStore } from "../stores/auth-store";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";

type ProtectedRouteProps = {
  role: User["role"];
  children: ReactNode;
};

export function ProtectedRoute({ role, children }: ProtectedRouteProps) {
  const authStore = role === "admin" ? useAdminAuthStore : useSellerAuthStore;
  const token = authStore((state) => state.token);
  const user = authStore((state) => state.user);
  const setSession = authStore((state) => state.setSession);
  const clear = authStore((state) => state.clear);
  const location = useLocation();
  const navigate = useNavigate();
  const loginPath = `/login?role=${role}&next=${encodeURIComponent(location.pathname)}`;

  const sessionQuery = useQuery({
    queryKey: ["auth-session", role, token],
    queryFn: () => api.me(token!),
    enabled: Boolean(token),
    retry: false
  });
  const withdrawApplication = useMutation({
    mutationFn: () => api.withdrawSellerApplication(token!),
    onSuccess: () => {
      clear();
      navigate("/login?role=seller", { replace: true });
    }
  });

  useEffect(() => {
    if (token && sessionQuery.data?.role === role) {
      setSession(token, sessionQuery.data);
    }
  }, [role, sessionQuery.data, setSession, token]);

  if (!token || !user) {
    return <Navigate to={loginPath} replace />;
  }

  if (user.role !== role) {
    clear();
    return <Navigate to={loginPath} replace />;
  }

  if (sessionQuery.isLoading || sessionQuery.isPending) {
    return <main className="container-pad py-8"><Skeleton className="h-96" /></main>;
  }

  if (sessionQuery.isError || !sessionQuery.data || sessionQuery.data.role !== role) {
    clear();
    return <Navigate to={loginPath} replace />;
  }

  const freshUser = sessionQuery.data;

  if (role === "seller" && freshUser.seller_status !== "approved") {
    return (
      <main className="container-pad flex min-h-[50vh] flex-col justify-center gap-2">
        <h1 className="text-2xl font-semibold">Seller approval pending</h1>
        <p className="text-muted-foreground">Your seller account is pending approval.</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={() => {
              clear();
              navigate("/login?role=seller", { replace: true });
            }}
          >
            Logout
          </Button>
          <Button
            variant="outline"
            disabled={withdrawApplication.isPending}
            onClick={() => withdrawApplication.mutate()}
          >
            Withdraw application
          </Button>
        </div>
      </main>
    );
  }

  return children;
}
